import { useEffect, useState } from 'react';
import { coachingApi } from '../services/api';
import { usePlayerAuth } from '../contexts/PlayerAuthContext';

export interface CoachClub {
  memberId: string;
  organizationId: string;
  organizationName: string;
  organizationSlug?: string;
}

const CACHE_KEY = 'coach-clubs-v1';

interface CacheShape {
  userId: string;
  clubs: CoachClub[];
}

function readCache(userId: string): CoachClub[] | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheShape;
    if (parsed.userId !== userId || !Array.isArray(parsed.clubs)) return null;
    return parsed.clubs;
  } catch {
    return null;
  }
}

function writeCache(userId: string, clubs: CoachClub[]) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ userId, clubs } satisfies CacheShape));
  } catch { /* storage full or unavailable */ }
}

/**
 * Whether the logged-in player has a coach record in any club.
 * Fetched once per session and cached, so headers can render the
 * coaching link without an extra request on every page.
 */
export function useIsCoach() {
  const { isLoggedIn, player } = usePlayerAuth();
  const userId = player?.userId || '';
  const [coachClubs, setCoachClubs] = useState<CoachClub[]>(() => (userId ? readCache(userId) || [] : []));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isLoggedIn || !userId) {
      setCoachClubs([]);
      return;
    }
    const cached = readCache(userId);
    if (cached) {
      setCoachClubs(cached);
      return;
    }
    let cancelled = false;
    setLoading(true);
    coachingApi.getMyCoachClubs()
      .then((res) => {
        if (cancelled || !res.data.success) return;
        const clubs = res.data.data.clubs || [];
        writeCache(userId, clubs);
        setCoachClubs(clubs);
      })
      .catch(() => {
        if (!cancelled) writeCache(userId, []);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [isLoggedIn, userId]);

  return { isCoach: coachClubs.length > 0, coachClubs, loading };
}

export default useIsCoach;
